/**
 * Sidebar menu builder — icon strings use keys from `design-system/layouts/sidebar.tsx` iconMap.
 * Links match `Routes/index.tsx`, nested route files, and `Routes/reports.tsx` only.
 */

import type { PlanFeatureKey } from 'Types/plan'
import { REPORTS_NAV_SECTIONS } from 'pages/Reports/reportsNavConfig'

export type SidebarMenuItem = {
 id?: string
 label: string
 icon?: string
 link?: string
 type?: 'section'
 /** Single permission, or OR-list (matches `PermissionGuard` behavior). */
 perm?: string | string[]
 preload?: string
 subItems?: SidebarMenuItem[]
 branchOnly?: boolean
 globalOnly?: boolean
 /** SaaS plan feature key — hidden when not included in tenant plan. */
 feature?: PlanFeatureKey
 /** Fall back to this item's authorized link when all optional children are hidden. */
 fallbackToLinkWhenChildrenHidden?: boolean
}

type HasPermission = (permission: string) => boolean

type ViewMode = 'global' | 'branch'

type HasFeature = (feature: PlanFeatureKey) => boolean

function normalizeViewMode(viewMode?: string): ViewMode {
 return viewMode === 'global' ? 'global' : 'branch'
}

function permAllows(
 isSuperAdmin: boolean,
 hasPermission: HasPermission,
 perm?: string | string[],
): boolean {
 if (isSuperAdmin) return true
 if (perm === undefined) return true
 const list = Array.isArray(perm) ? perm : [perm]
 return list.some((p) => hasPermission(p))
}

function viewAllows(item: SidebarMenuItem, mode: ViewMode): boolean {
 if (item.branchOnly && mode !== 'branch') return false
 if (item.globalOnly && mode !== 'global') return false
 return true
}

function featureAllows(hasFeature: HasFeature, feature?: PlanFeatureKey): boolean {
 if (!feature) return true
 return hasFeature(feature)
}

const filterItems = (
 items: SidebarMenuItem[],
 isSuperAdmin: boolean,
 hasPermission: HasPermission,
 hasFeature: HasFeature,
 mode: ViewMode,
): SidebarMenuItem[] => {
 const filtered = items
 .map((item) => {
 if (item.type === 'section') {
 return item
 }
 if (!viewAllows(item, mode)) {
 return null
 }

 if (!featureAllows(hasFeature, item.feature)) {
 return null
 }

 const subItems = item.subItems
 ? filterItems(item.subItems, isSuperAdmin, hasPermission, hasFeature, mode)
 : undefined

 const hasDirectAccess = permAllows(isSuperAdmin, hasPermission, item.perm)

 const hasChildren = Boolean(subItems && subItems.length > 0)

 if (!hasDirectAccess && !hasChildren) {
 return null
 }

 const childrenHidden = Boolean(item.subItems && !hasChildren)

 if (childrenHidden && !item.fallbackToLinkWhenChildrenHidden) {
 return null
 }

 return {
 ...item,
 subItems: childrenHidden ? undefined : subItems,
 }
 })

 const result: SidebarMenuItem[] = []
 let pendingSection: SidebarMenuItem | null = null

 for (const item of filtered) {
  if (!item) continue
  if (item.type === 'section') {
   pendingSection = item
   continue
  }
  if (pendingSection) {
   result.push(pendingSection)
   pendingSection = null
  }
  result.push(item)
 }

 return result
}

const MARKETING_PERM: string[] = ['manage_promotions', 'manage_coupons', 'manage_gift_cards']

const DELIVERY_FINANCE_PERM: string[] = [
 'delivery_view_settlements',
 'delivery_settle',
 'manage_deliveries',
]

function buildReportsSidebarSubItems(t: (key: string) => string): SidebarMenuItem[] {
 const items: SidebarMenuItem[] = []

 for (const section of REPORTS_NAV_SECTIONS) {
  items.push({ type: 'section', label: t(section.labelKey) })
  for (const item of section.items) {
   items.push({
    id: item.id,
    label: t(item.navLabelKey ?? item.labelKey),
    icon: item.sidebarIcon,
    link: `/reports/${item.path}`,
    perm: 'view_reports',
    ...(item.feature ? { feature: item.feature } : {}),
   })
  }
 }

 return items
}

export function buildSidebarMenu(
 isSuperAdmin: boolean,
 hasPermission: HasPermission,
 viewMode?: string,
 hasFeature: HasFeature = () => true,
 t: (key: string) => string = (key) => key,
): SidebarMenuItem[] {
 const mode = normalizeViewMode(viewMode)

 const menu: SidebarMenuItem[] = [
 {
 id: 'nav-dashboard',
 label: t('nav.dashboard'),
 icon: 'las la-tachometer-alt',
 link: '/',
 },
 {
 id: 'nav-sales-workspace',
 label: t('nav.salesWorkspace'),
 icon: 'ri-shopping-cart-line',
 subItems: [
 {
 id: 'nav-pos-screen',
 label: t('nav.pos.screen'),
 icon: 'ri-shopping-cart-line',
 link: '/pos',
 perm: 'process_sales',
 branchOnly: true,
 feature: 'pos',
 },
 {
 id: 'nav-waiter',
 label: t('nav.waiter'),
 icon: 'ri-restaurant-line',
 link: '/waiter',
 perm: ['process_sales', 'manage_dining'],
 branchOnly: true,
 feature: 'pos',
 },
 {
 id: 'nav-sales',
 label: t('nav.sales'),
 icon: 'ri-receipt-line',
 link: '/sales',
 perm: 'process_sales',
 branchOnly: true,
 feature: 'pos',
 },
 {
 id: 'nav-sales-products',
 label: t('nav.sales.products'),
 icon: 'las la-boxes',
 link: '/sales/products',
 perm: 'process_sales',
 branchOnly: true,
 feature: 'pos',
 },
 {
 id: 'nav-sales-layaway',
 label: t('nav.sales.layaway'),
 icon: 'ri-time-line',
 link: '/sales/layaway',
 perm: 'process_sales',
 branchOnly: true,
 feature: 'pos',
 },
 {
 id: 'nav-sales-returns',
 label: t('nav.sales.returns'),
 icon: 'ri-clipboard-fill',
 link: '/sales/returns',
 perm: 'void_receipt',
 branchOnly: true,
 feature: 'pos',
 },
 {
 id: 'nav-customers-list',
 label: t('nav.customers'),
 icon: 'ri-user-line',
 link: '/customers',
 perm: ['manage_customers', 'process_sales'],
 },
 ],
 },
 {
 id: 'nav-purchases',
 label: t('nav.purchases'),
 icon: 'ri-truck-line',
 feature: 'purchases',
 subItems: [
 {
 id: 'nav-purchases-list',
 label: t('nav.purchases.invoices'),
 icon: 'ri-file-list-line',
 link: '/purchases',
 perm: 'manage_purchases',
 },
 {
 id: 'nav-purchases-returns',
 label: t('nav.purchases.returns'),
 icon: 'ri-clipboard-fill',
 link: '/purchases/returns',
 perm: 'manage_purchases',
 },
 {
 id: 'nav-purchases-suppliers-nested',
 label: t('nav.purchases.suppliers'),
 icon: 'ri-user-line',
 link: '/purchases/suppliers',
 perm: 'manage_purchases',
 },
 {
 id: 'nav-suppliers',
 label: t('nav.suppliers'),
 icon: 'ri-user-line',
 link: '/suppliers',
 perm: 'manage_suppliers',
 },
 ],
 },
 {
 id: 'nav-inventory',
 label: t('nav.inventory'),
 icon: 'las la-boxes',
 feature: 'inventory',
 subItems: [
 {
 id: 'nav-products',
 label: t('nav.products'),
 icon: 'las la-boxes',
 link: '/products',
 perm: 'manage_products',
 },
 {
 id: 'nav-raw-materials',
 label: t('nav.rawMaterials'),
 icon: 'ri-flask-line',
 link: '/raw-materials',
 perm: 'manage_products',
 },
 {
 id: 'nav-categories',
 label: t('nav.categories'),
 icon: 'ri-folder-line',
 link: '/categories',
 perm: 'manage_categories',
 },
 {
 id: 'nav-inventory-main',
 label: t('nav.inventory.overview'),
 icon: 'ri-store-2-line',
 link: '/inventory',
 perm: 'manage_inventory',
 },
 {
 id: 'nav-inventory-balances',
 label: t('nav.inventory.balances'),
 icon: 'ri-file-list-line',
 link: '/inventory/balances',
 perm: 'manage_inventory',
 },
 {
 id: 'nav-inventory-warehouses',
 label: t('nav.inventory.warehouses'),
 icon: 'ri-store-2-line',
 link: '/inventory/warehouses',
 perm: 'manage_inventory',
 },
 {
 id: 'nav-inventory-movements',
 label: t('nav.inventory.movements'),
 icon: 'ri-clipboard-fill',
 link: '/inventory/movements',
 perm: 'manage_inventory',
 },
 {
 id: 'nav-inventory-reorder',
 label: t('nav.inventory.reorder'),
 icon: 'ri-file-list-3-line',
 link: '/inventory/reorder-rules',
 perm: 'manage_inventory',
 },
 {
 id: 'nav-inventory-requisitions',
 label: t('nav.inventory.requisitions'),
 icon: 'ri-shopping-bag-line',
 link: '/inventory/requisitions',
 perm: 'manage_inventory',
 },
 {
 id: 'nav-inventory-stock-counts',
 label: t('nav.inventory.stockCounts'),
 icon: 'ri-clipboard-fill',
 link: '/inventory/stock-counts',
 perm: 'manage_inventory',
 },
 {
 id: 'nav-inventory-transfer',
 label: t('nav.inventory.transfers'),
 icon: 'ri-node-tree',
 link: '/inventory/transfers',
 perm: 'manage_inventory',
 },
 {
 id: 'nav-inventory-adjustment',
 label: t('nav.inventory.adjustments'),
 icon: 'ri-clipboard-fill',
 link: '/inventory/adjustments',
 perm: 'manage_inventory',
 },
 ],
 },
 {
 id: 'nav-delivery',
 label: t('nav.delivery'),
 icon: 'ri-truck-line',
 subItems: [
 {
 id: 'nav-delivery-orders',
 label: t('nav.delivery.orders'),
 icon: 'ri-truck-line',
 link: '/delivery',
 perm: ['process_sales', 'manage_deliveries'],
 },
 {
 id: 'nav-drivers',
 label: t('nav.drivers'),
 icon: 'ri-user-line',
 link: '/drivers',
 perm: 'manage_drivers',
 },
 {
 id: 'nav-delivery-zones',
 label: t('nav.delivery.zones'),
 icon: 'ri-layout-grid-line',
 link: '/delivery-zones',
 perm: 'manage_deliveries',
 },
 ],
 },
 {
 id: 'nav-operations',
 label: t('nav.operations'),
 icon: 'ri-restaurant-line',
 subItems: [
 {
 id: 'nav-dining-halls',
 label: t('nav.diningHalls'),
 icon: 'ri-building-line',
 link: '/dining-halls',
 perm: 'manage_dining',
 branchOnly: true,
 },
 {
 id: 'nav-kitchen-display',
 label: t('nav.kitchen.display'),
 icon: 'ri-restaurant-line',
 link: '/kitchen',
 perm: ['view_kitchen', 'manage_kitchen'],
 branchOnly: true,
 },
 {
 id: 'nav-kitchen-print-jobs',
 label: t('nav.kitchen.printJobs'),
 icon: 'ri-printer-line',
 link: '/kitchen/print-jobs',
 perm: ['view_kitchen', 'manage_kitchen'],
 branchOnly: true,
 },
 {
 id: 'nav-kitchen-stations',
 label: t('nav.kitchen.stations'),
 icon: 'ri-layout-grid-line',
 link: '/kitchen/stations',
 perm: 'manage_kitchen',
 branchOnly: true,
 },
 ],
 },
 {
 id: 'nav-finance',
 label: t('nav.finance'),
 icon: 'ri-bank-line',
 subItems: [
 { type: 'section', label: t('nav.finance.sections.planning') },
 {
 id: 'nav-expenses',
 label: t('nav.expenses'),
 icon: 'ri-bank-line',
 link: '/expenses',
 perm: ['process_sales', 'manage_expenses'],
 },
 {
 id: 'nav-budgets',
 label: t('nav.budgets'),
 icon: 'ri-funds-line',
 link: '/budgets',
 perm: ['view_budgets', 'manage_budgets'],
 },
 { type: 'section', label: t('nav.finance.sections.cash') },
 {
 id: 'nav-shifts',
 label: t('nav.shifts'),
 icon: 'ri-time-line',
 link: '/shifts',
 perm: ['manage_shifts', 'access_admin_routes'],
 },
 {
 id: 'nav-financial-accounts',
 label: t('nav.financialAccounts'),
 icon: 'ri-wallet-3-line',
 link: '/financial-accounts',
 perm: ['view_account_balances', 'manage_financial_accounts', 'manage_treasuries', 'access_admin_routes'],
 },
 {
 id: 'nav-payments',
 label: t('nav.payments'),
 icon: 'ri-bank-line',
 link: '/payments',
 perm: 'manage_treasuries',
 },
 { type: 'section', label: t('nav.finance.sections.settlements') },
 {
 id: 'nav-supplier-payments',
 label: t('nav.supplierPayments'),
 icon: 'ri-money-dollar-circle-line',
 link: '/supplier-payments',
 perm: 'manage_supplier_payments',
 },
 {
 id: 'nav-driver-settlements',
 label: t('nav.driverSettlements'),
 icon: 'ri-receipt-line',
 link: '/driver-settlements',
 perm: DELIVERY_FINANCE_PERM,
 },
 {
 id: 'nav-delivery-finance',
 label: t('nav.deliveryFinance'),
 icon: 'ri-file-list-3-line',
 link: '/delivery-finance',
 perm: DELIVERY_FINANCE_PERM,
 },
 {
 id: 'nav-delivery-finance-liabilities',
 label: t('nav.deliveryFinance.liabilities'),
 icon: 'ri-bank-line',
 link: '/delivery-finance/liabilities',
 perm: DELIVERY_FINANCE_PERM,
 },
 {
 id: 'nav-delivery-finance-settlements',
 label: t('nav.deliveryFinance.settlements'),
 icon: 'ri-receipt-line',
 link: '/delivery-finance/settlements',
 perm: DELIVERY_FINANCE_PERM,
 },
 {
 id: 'nav-delivery-finance-alerts',
 label: t('nav.deliveryFinance.alerts'),
 icon: 'ri-file-list-line',
 link: '/delivery-finance/alerts',
 perm: DELIVERY_FINANCE_PERM,
 },
 ],
 },
 {
 id: 'nav-marketing',
 label: t('nav.marketing'),
 icon: 'ri-price-tag-3-line',
 perm: MARKETING_PERM,
 subItems: [
 {
 id: 'nav-marketing-promotions',
 label: t('nav.marketing.promotions'),
 icon: 'ri-price-tag-3-line',
 link: '/marketing/promotions',
 perm: 'manage_promotions',
 },
 {
 id: 'nav-marketing-coupons',
 label: t('nav.marketing.coupons'),
 icon: 'ri-file-list-line',
 link: '/marketing/coupons',
 perm: 'manage_coupons',
 feature: 'coupons',
 },
 {
 id: 'nav-gift-cards',
 label: t('nav.giftCards'),
 icon: 'ri-gift-line',
 link: '/gift-cards',
 perm: 'manage_gift_cards',
 },
 ],
 },
 {
 id: 'nav-intelligence',
 label: t('nav.intelligence'),
 icon: 'ri-sparkling-line',
 link: '/intelligence',
 perm: 'view_reports',
 fallbackToLinkWhenChildrenHidden: true,
  subItems: [
   {
    id: 'nav-intelligence-targets',
    label: t('nav.intelligence.targets'),
    icon: 'ri-focus-3-line',
    link: '/intelligence/targets',
    perm: 'view_reports',
    feature: 'intelligence_targets',
   },
  ],
 },
 {
 id: 'nav-reports',
 label: t('nav.reports'),
 icon: 'ri-file-list-3-line',
 perm: 'view_reports',
 feature: 'reports',
 subItems: [
  { id: 'nav-reports-hub', label: t('nav.reports.hub'), icon: 'ri-file-list-3-line', link: '/reports', perm: 'view_reports' },
  ...buildReportsSidebarSubItems(t),
 ],
 },
 {
 id: 'nav-employees',
 label: t('nav.employees'),
 icon: 'ri-shield-user-line',
 subItems: [
 {
 id: 'nav-employees-staff',
 label: t('employees.tabs.staff'),
 icon: 'ri-shield-user-line',
 link: '/employees',
 perm: ['manage_employees', 'manage_users'],
 },
 {
 id: 'nav-employees-attendance',
 label: t('employees.tabs.attendance'),
 icon: 'ri-calendar-check-line',
 link: '/employees?tab=attendance',
 perm: 'manage_attendance',
 },
 {
 id: 'nav-employees-attendance-records',
 label: t('employees.tabs.attendanceRecords'),
 icon: 'ri-file-list-3-line',
 link: '/employees?tab=attendance-records',
 perm: 'manage_attendance',
 },
 {
 id: 'nav-employees-adjustments',
 label: t('employees.tabs.adjustments'),
 icon: 'ri-funds-line',
 link: '/employees?tab=adjustments',
 perm: 'manage_payroll_adjustments',
 },
 {
 id: 'nav-employees-payroll',
 label: t('employees.tabs.payroll'),
 icon: 'ri-money-dollar-circle-line',
 link: '/employees?tab=payroll',
 perm: ['view_payroll', 'manage_payroll'],
 },
 {
 id: 'nav-employees-access',
 label: t('employees.tabs.access'),
 icon: 'ri-user-settings-line',
 link: '/employees?tab=access',
 perm: 'manage_users',
 },
 {
 id: 'nav-employees-roles',
 label: t('employees.tabs.roles'),
 icon: 'ri-key-2-line',
 link: '/employees?tab=roles',
 perm: 'manage_users',
 },
 ],
 },
 {
 id: 'nav-system',
 label: t('nav.system'),
 icon: 'ri-settings-line',
 subItems: [
 {
 id: 'nav-settings',
 label: t('nav.settings'),
 icon: 'ri-settings-line',
 link: '/settings',
 perm: 'manage_settings',
 },
 {
 id: 'nav-branches',
 label: t('nav.branches'),
 icon: 'ri-building-line',
 link: '/branches',
 perm: 'manage_branches',
 globalOnly: true,
 feature: 'branches',
 },
 {
 id: 'nav-activity-logs',
 label: t('nav.activityLogs'),
 icon: 'ri-file-list-3-line',
 link: '/activity-logs',
 perm: 'view_activity_logs',
 },
 {
 id: 'nav-client-diagnostics',
 label: t('nav.clientDiagnostics'),
 icon: 'ri-bug-line',
 link: '/client-diagnostics',
 perm: 'view_activity_logs',
 },
 {
 id: 'nav-settingsBackup',
 label: t('nav.settingsBackup'),
 icon: 'ri-database-2-line',
 link: '/backup',
 perm: 'manage_settings',
 globalOnly: true,
 },
 {
 id: 'nav-importExport',
  label: t('nav.importExport'),
  icon: 'ri-file-transfer-line',
  link: '/import-export',
 },
 ],
 },
 ]

 return filterItems(menu, isSuperAdmin, hasPermission, hasFeature, mode)
}
